import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useWindowWidth } from '../hooks/useWindowWidth';

export const GUIDE_SECTIONS = [
  { path: '/guide',                    label: 'Guide overview' },
  { path: '/guide/what-is-a-tender',   label: 'What is a public tender?' },
  { path: '/guide/who-can-bid',        label: 'Who can bid?' },
  { path: '/guide/bidding-process',    label: 'The bidding process' },
  { path: '/guide/winning-a-bid',      label: 'What makes a winning bid?' },
  { path: '/guide/eu-rules',           label: 'EU procurement rules' },
  { path: '/guide/glossary',           label: 'Glossary' },
];

const s = {
  outer: { minHeight: '100vh', background: '#f8fafc' },
  body: {
    maxWidth: 1080,
    margin: '0 auto',
    padding: '40px 24px 80px',
    display: 'flex',
    gap: 48,
    alignItems: 'flex-start',
  },
  sidebar: {
    width: 220,
    flexShrink: 0,
    position: 'sticky',
    top: 80,
  },
  sidebarLabel: {
    fontSize: 11, fontWeight: 700, color: '#94a3b8',
    textTransform: 'uppercase', letterSpacing: '0.08em',
    marginBottom: 12,
  },
  sidebarLink: (active) => ({
    display: 'block',
    fontSize: 14,
    color: active ? '#1e40af' : '#475569',
    fontWeight: active ? 600 : 400,
    padding: '7px 12px',
    borderRadius: 6,
    background: active ? '#eff6ff' : 'transparent',
    textDecoration: 'none',
    marginBottom: 2,
    borderLeft: active ? '3px solid #2563eb' : '3px solid transparent',
  }),
  content: { flex: 1, minWidth: 0 },
  mobileSidebarToggle: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
    padding: '10px 16px', fontSize: 14, color: '#374151', fontWeight: 500,
    cursor: 'pointer', width: '100%', textAlign: 'left',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 16,
  },
  mobileSidebarMenu: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
    padding: '8px', marginBottom: 20,
  },
  mobileSidebarLink: (active) => ({
    display: 'block', fontSize: 14,
    color: active ? '#1e40af' : '#374151',
    fontWeight: active ? 600 : 400,
    padding: '9px 12px', borderRadius: 6,
    background: active ? '#eff6ff' : 'transparent',
    textDecoration: 'none', marginBottom: 2,
  }),
  cta: {
    background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
    borderRadius: 12, padding: '32px 28px', marginTop: 48, textAlign: 'center',
  },
  ctaTitle: { fontSize: 20, fontWeight: 700, color: '#fff', marginBottom: 8 },
  ctaText: { fontSize: 14, color: 'rgba(255,255,255,0.8)', marginBottom: 20, lineHeight: 1.6 },
  ctaButton: {
    display: 'inline-block',
    background: '#fff', color: '#1e40af', fontWeight: 700,
    fontSize: 15, padding: '11px 28px', borderRadius: 8, textDecoration: 'none',
  },
};

export function InfoBox({ children }) {
  return (
    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '14px 18px', marginBottom: 24, fontSize: 14, color: '#1e40af', lineHeight: 1.6 }}>
      {children}
    </div>
  );
}

export function SectionCTA() {
  return (
    <div style={s.cta}>
      <div style={s.ctaTitle}>Ready to find tenders that match your company?</div>
      <div style={s.ctaText}>Jyto processes every new EU tender daily and uses AI to surface the contracts most relevant to your profile — so you start each day with your best opportunities already ranked.</div>
      <Link to="/signup" style={s.ctaButton}>Try Jyto free</Link>
    </div>
  );
}

export default function GuideLayout({ children }) {
  const { pathname } = useLocation();
  const width = useWindowWidth();
  const isMobile = width < 768;
  const [menuOpen, setMenuOpen] = useState(false);

  const currentSection = GUIDE_SECTIONS.find((s) => s.path === pathname);

  return (
    <div style={s.outer}>

      <div style={isMobile ? { maxWidth: 1080, margin: '0 auto', padding: '24px 16px 60px' } : s.body}>
        {isMobile ? (
          <>
            <button style={s.mobileSidebarToggle} onClick={() => setMenuOpen((o) => !o)}>
              <span>Tender Guide: {currentSection ? currentSection.label : 'Navigation'}</span>
              <span>{menuOpen ? '▲' : '▼'}</span>
            </button>
            {menuOpen && (
              <div style={s.mobileSidebarMenu}>
                {GUIDE_SECTIONS.map((sec) => (
                  <Link
                    key={sec.path}
                    to={sec.path}
                    style={s.mobileSidebarLink(pathname === sec.path)}
                    onClick={() => setMenuOpen(false)}
                  >
                    {sec.label}
                  </Link>
                ))}
              </div>
            )}
          </>
        ) : (
          <nav style={s.sidebar}>
            <div style={s.sidebarLabel}>Tender Guide</div>
            {GUIDE_SECTIONS.map((sec) => (
              <Link key={sec.path} to={sec.path} style={s.sidebarLink(pathname === sec.path)}>
                {sec.label}
              </Link>
            ))}
          </nav>
        )}

        <main style={s.content}>
          {children}
        </main>
      </div>
    </div>
  );
}
